/**
  * Make a reducer function from namespaced handlers.
  * @param {object} handlers - handlers grouped by namespace.
  * @return {function} - reducer for the store.
  */
const createReducer = (handlers) => {
  if (!handlers || handlers.constructor !== Object) {
    throw new TypeError('Expected handlers to be an object.');
  }
  const namespaces = Object.keys(handlers);
  const handlersIndex = namespaces.reduce((o, namespace) => {
    for (let actionType in handlers[namespace]) {
      const reducers = o[actionType] =  o[actionType] || [];
      const reducer = handlers[namespace][actionType];
      const namespacedReducer = (state, action) => {
        state[namespace] = reducer(state[namespace], action)
        return Object.assign({}, state);
      };
      if (actionType === '__INIT') o['__INIT@' + namespace] = [namespacedReducer];
      reducers.push(namespacedReducer);
    }
    return o;
  }, {});
  // hack for Redux compatibility
  handlersIndex['@@redux/INIT'] = handlersIndex['__INIT'];
  return (state = {}, action) => {
    const reducers = handlersIndex[action.type];
    if (!reducers) throw new Error(`Invalid action type "${action.type}".`);
    reducers.forEach(reducer => reducer(state, action));
    return state;
  };
};

/**
  * Create a data store to manage application state.
  * @param {object} reducers - reducers grouped by namespace.
  * @return {function} - reducer for the store.
  */
const createStore = (reducer, initialState = {}, enhancer) => {
  // Enhance store is an enhancer is given.
  if (enhancer) {
    if (typeof enhancer !== 'function') {
      throw new TypeError('Expected enhancer to be a function');
    }
    return enhancer(createStore)(reducer, initialState);
  }
  let state = initialState;
  // Return the current state;
  const getState = () => state;
  // Mutate state using reducer and the give flux standard action.
  let isDispatching = false;
  const dispatch = (action) => {
    if (isDispatching) {
      throw new Error('Reducers should not dispatch actions.');
    }
    try {
      isDispatching = true;
      if (typeof action.type !== 'string') {
        throw new TypeError('Expected type to be a string.');
      }
      if (action.meta && action.meta.constructor !== Object) {
        throw new TypeError('Expected meta to be an object.');
      }
      state = reducer(state, action);
      listeners.forEach((listener) => listener());
    } finally {
      isDispatching = false
    }
    return action;
  };
  // Add function to call each time dispatch is called.
  const listeners = [];
  const subscribe = (listener) => {
    if (typeof listener !== 'function') {
      throw new TypeError('Expected listener to be a function');
    }
    listeners.push(listener);
    return () => {
      const index = listeners.indexOf(listener);
      if (index < 0) return;
      listeners.splice(index, 1);
    };
  };
  // Set the initial state
  dispatch({type: '__INIT'});
  // create store object.
  return {getState, dispatch, subscribe};
};

module.exports = {createReducer, createStore};



// WEBPACK FOOTER //
// ../rex/lib/store.js